import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  FEEDBACK_MILESTONE_EVENT,
  captureFeedbackEvent,
  getAccountCreatedAt,
  hasFeedbackMilestone,
  hasSurveyState,
  identifyFeedbackUser,
  resetFeedbackUser,
  saveSurveyState,
} from 'utils/feedbackAnalytics';
import {
  isOnboardingInProgress,
  shouldShowPlatformOnboarding,
} from 'components/onboarding/platformOnboardingUtils';
import './PostHogFeedbackSurvey.css';

const DAY_IN_MS = 24 * 60 * 60 * 1000;
const SURVEY_COOLDOWN_MS = 10 * 1000;
const hasVisibleModal = () =>
  Array.from(document.querySelectorAll('.ant-modal-wrap')).some((element) => {
    const style = window.getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden';
  });
const isSurveyBlocked = () =>
  isOnboardingInProgress() || shouldShowPlatformOnboarding() || hasVisibleModal();

const SURVEYS = {
  agent_created: {
    id: 'agent-creation-ces-v1',
    type: 'CES',
    title: 'Quick question',
    question: 'How easy was it to create your AI agent?',
    lowLabel: 'Very difficult',
    highLabel: 'Very easy',
    scores: [1, 2, 3, 4, 5],
    commentPrompt: 'What would make setup easier? (optional)',
  },
  first_call_completed: {
    id: 'first-call-csat-v1',
    type: 'CSAT',
    title: 'How did it go?',
    question: 'How satisfied are you with your first completed call?',
    lowLabel: 'Not satisfied',
    highLabel: 'Very satisfied',
    scores: [1, 2, 3, 4, 5],
    commentPrompt: 'Tell us what worked or what did not. (optional)',
  },
  account_day_7: {
    id: 'day-7-nps-v1',
    type: 'NPS',
    title: 'Help us improve VEE Lite',
    question: 'How likely are you to recommend VEE Lite to a friend or colleague?',
    lowLabel: 'Not likely',
    highLabel: 'Very likely',
    scores: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    commentPrompt: 'What is the main reason for your score? (optional)',
  },
};

const PostHogFeedbackSurvey = () => {
  const token = useSelector((state) => state.auth.token);
  const hadTokenRef = useRef(Boolean(token));
  const [queue, setQueue] = useState([]);
  const [activeSurvey, setActiveSurvey] = useState(null);
  const [score, setScore] = useState(null);
  const [comment, setComment] = useState('');
  const [lastSurveyClosedAt, setLastSurveyClosedAt] = useState(0);

  const enqueueSurvey = useCallback((milestone) => {
    const survey = SURVEYS[milestone];
    if (!survey || hasSurveyState(survey.id)) return;

    setQueue((current) => {
      if (current.some((item) => item.id === survey.id)) return current;
      return [...current, { ...survey, milestone }];
    });
  }, []);

  useEffect(() => {
    if (!token) {
      setQueue([]);
      setActiveSurvey(null);
      setLastSurveyClosedAt(0);
      // Only reset on real logout so anonymous login/signup pageviews keep one distinct id.
      if (hadTokenRef.current) {
        resetFeedbackUser();
      }
      hadTokenRef.current = false;
      return undefined;
    }

    hadTokenRef.current = true;
    const user = identifyFeedbackUser();
    const handleMilestone = (event) => {
      if (event.detail?.isFirstOccurrence) {
        enqueueSurvey(event.detail.milestone);
      }
    };

    window.addEventListener(FEEDBACK_MILESTONE_EVENT, handleMilestone);

    if (hasFeedbackMilestone('agent_created')) enqueueSurvey('agent_created');
    if (hasFeedbackMilestone('first_call_completed')) enqueueSurvey('first_call_completed');

    let daySevenTimer;
    const accountCreatedAt = getAccountCreatedAt(user?.payload);
    if (accountCreatedAt && !hasSurveyState(SURVEYS.account_day_7.id)) {
      const delay = accountCreatedAt.getTime() + 7 * DAY_IN_MS - Date.now();
      if (delay <= 0) {
        enqueueSurvey('account_day_7');
      } else {
        daySevenTimer = window.setTimeout(
          () => enqueueSurvey('account_day_7'),
          Math.min(delay, 2147483647)
        );
      }
    }

    return () => {
      window.removeEventListener(FEEDBACK_MILESTONE_EVENT, handleMilestone);
      if (daySevenTimer) window.clearTimeout(daySevenTimer);
    };
  }, [enqueueSurvey, token]);

  useEffect(() => {
    if (activeSurvey || queue.length === 0) {
      return undefined;
    }

    let showTimer;
    const remainingCooldown = Math.max(
      0,
      SURVEY_COOLDOWN_MS - (Date.now() - lastSurveyClosedAt)
    );
    const tryToShowSurvey = (delay) => {
      showTimer = window.setTimeout(() => {
        if (isSurveyBlocked()) {
          tryToShowSurvey(1500);
          return;
        }

        const [nextSurvey, ...remaining] = queue;
        setQueue(remaining);
        setActiveSurvey(nextSurvey);
        setScore(null);
        setComment('');
        captureFeedbackEvent('feedback_survey_shown', {
          survey_id: nextSurvey.id,
          survey_type: nextSurvey.type,
          milestone: nextSurvey.milestone,
        });
      }, delay);
    };

    tryToShowSurvey(Math.max(1500, remainingCooldown));

    return () => window.clearTimeout(showTimer);
  }, [activeSurvey, lastSurveyClosedAt, queue]);

  const eventProperties = useMemo(() => {
    if (!activeSurvey) return {};
    return {
      survey_id: activeSurvey.id,
      survey_type: activeSurvey.type,
      milestone: activeSurvey.milestone,
    };
  }, [activeSurvey]);

  const dismiss = () => {
    saveSurveyState(activeSurvey.id, 'dismissed');
    captureFeedbackEvent('feedback_survey_dismissed', eventProperties);
    setLastSurveyClosedAt(Date.now());
    setActiveSurvey(null);
  };

  const submit = (event) => {
    event.preventDefault();
    if (score === null) return;

    saveSurveyState(activeSurvey.id, 'submitted');
    captureFeedbackEvent('feedback_survey_submitted', {
      ...eventProperties,
      score,
      comment: comment.trim() || undefined,
    });
    setLastSurveyClosedAt(Date.now());
    setActiveSurvey(null);
  };

  if (!token || !activeSurvey) return null;

  return (
    <aside className="ph-feedback" role="dialog" aria-modal="false" aria-labelledby="ph-feedback-title">
      <button className="ph-feedback__close" type="button" aria-label="Dismiss survey" onClick={dismiss}>
        ×
      </button>
      <form onSubmit={submit}>
        <p className="ph-feedback__eyebrow">{activeSurvey.type}</p>
        <h2 id="ph-feedback-title" className="ph-feedback__title">{activeSurvey.title}</h2>
        <p className="ph-feedback__question">{activeSurvey.question}</p>

        <div className={`ph-feedback__scores ph-feedback__scores--${activeSurvey.type.toLowerCase()}`}>
          {activeSurvey.scores.map((value) => (
            <button
              key={value}
              type="button"
              className={`ph-feedback__score${score === value ? ' is-selected' : ''}`}
              aria-pressed={score === value}
              onClick={() => setScore(value)}
            >
              {value}
            </button>
          ))}
        </div>
        <div className="ph-feedback__labels" aria-hidden="true">
          <span>{activeSurvey.lowLabel}</span>
          <span>{activeSurvey.highLabel}</span>
        </div>

        {score !== null && (
          <label className="ph-feedback__comment">
            <span>{activeSurvey.commentPrompt}</span>
            <textarea
              rows="2"
              maxLength="500"
              value={comment}
              onChange={(event) => setComment(event.target.value)}
            />
          </label>
        )}

        <div className="ph-feedback__actions">
          <button type="button" className="ph-feedback__skip" onClick={dismiss}>Not now</button>
          <button type="submit" className="ph-feedback__submit" disabled={score === null}>Send feedback</button>
        </div>
      </form>
    </aside>
  );
};

export default PostHogFeedbackSurvey;
